import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Send, Paperclip, MessageCircle, Clock, Sparkles, Plus, Trash2, Edit3, MoreVertical } from 'lucide-react';
import { supabase } from '../supabaseClient';
import ReactMarkdown from 'react-markdown';
import './LandingPage.css';

/**
 * Get a greeting based on the time of day
 */
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

/**
 * Group chats by time period for sidebar display
 */
function groupChatsByTime(chats) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const groups = { Today: [], Yesterday: [], 'Previous 7 Days': [], Earlier: [] };

  chats.forEach((chat) => {
    const d = new Date(chat.updated_at || chat.created_at);
    if (d >= today) groups['Today'].push(chat);
    else if (d >= yesterday) groups['Yesterday'].push(chat);
    else if (d >= sevenDaysAgo) groups['Previous 7 Days'].push(chat);
    else groups['Earlier'].push(chat);
  });

  return Object.entries(groups).filter(([, items]) => items.length > 0);
}

export default function LandingPage({ onNavigate }) {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'success' | 'error'
  const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth > 1024);

  // Prompt suggestions
  const promptSuggestions = [
    "What are my rights as a tenant?",
    "Explain contract law basics",
    "Help me draft a legal notice"
  ];

  const userName = user?.name || user?.email?.split('@')[0] || 'User';

  // Load chats from backend
  useEffect(() => {
    loadChats();
    loadProfilePhoto();
  }, []);

  // Load user profile photo
  const loadProfilePhoto = async () => {
    if (user?.email) {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('photo_url')
          .eq('email', user.email)
          .single();
        if (data && data.photo_url) {
          setProfilePhoto(data.photo_url);
        }
      } catch (error) {
        console.error('Error loading profile photo:', error);
      }
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.history-actions')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const loadChats = async () => {
    try {
      const { data: sessions, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // For each session, fetch the last message
      const sessionsWithMessages = await Promise.all(
        (sessions || []).map(async (session) => {
          const { data: messages } = await supabase
            .from('chat_messages')
            .select('content')
            .eq('session_id', session.id)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);

          const lastMessage = messages && messages[0] ? messages[0].content : '';
          return {
            ...session,
            last_message: lastMessage ? (lastMessage.substring(0, 80) + (lastMessage.length > 80 ? '...' : '')) : 'No messages'
          };
        })
      );

      setChats(sessionsWithMessages);
    } catch (error) {
      console.error('Error loading chats:', error);
    }
  };

  const handleNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    setShowWelcome(true);
  };

  const handleChatSelect = async (chatId) => {
    setActiveChatId(chatId);
    setShowWelcome(false);
    if (window.innerWidth <= 1024) {
      setSidebarVisible(false); // Only auto-close on mobile
    }

    try {
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', chatId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Map to the format the UI expects
      const formattedMessages = (messages || []).map(msg => ({
        role: msg.role,
        text: msg.content,
        timestamp: msg.created_at
      }));

      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handleDeleteChat = async (chatId, e) => {
    if (e) e.stopPropagation();
    setOpenMenuId(null);
    try {
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;

      loadChats();
      if (activeChatId === chatId) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const handleRenameChat = (chat, e) => {
    if (e) e.stopPropagation();
    setRenamingChatId(chat.id);
    setRenameValue(chat.title || 'Untitled Chat');
    setOpenMenuId(null);
  };

  const handleRenameSubmit = async (chatId, e) => {
    if (e) e.preventDefault();
    if (!renameValue.trim()) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .update({ title: renameValue.trim() })
        .eq('id', chatId)
        .eq('user_id', user.id);

      if (error) throw error;

      loadChats();
      setRenamingChatId(null);
      setRenameValue('');
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  };

  const handleRenameCancel = () => {
    setRenamingChatId(null);
    setRenameValue('');
  };

  const toggleMenu = (chatId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === chatId ? null : chatId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    const userText = query;
    setQuery('');
    setIsLoading(true);
    setShowWelcome(false);

    // Save to local UI state
    const userMsg = { role: 'user', text: userText, timestamp: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);

    let currentSessionId = activeChatId;

    try {
      // 1. If no active session, create one first in Supabase
      if (!currentSessionId) {
        const title = userText.substring(0, 50) + (userText.length > 50 ? '...' : '');
        const { data: sessionData, error: sessionError } = await supabase
          .from('chat_sessions')
          .insert([{ 
            title,
            user_id: user.id
          }])
          .select()
          .single();

        if (sessionError) throw sessionError;
        currentSessionId = sessionData.id;
        setActiveChatId(currentSessionId);
        loadChats(); // Refresh history sidebar
      }

      // 2. Save user message to Supabase
      const { error: userMsgError } = await supabase
        .from('chat_messages')
        .insert([{
          session_id: currentSessionId,
          role: 'user',
          content: userText,
          user_id: user.id
        }]);

      if (userMsgError) console.error('Error saving user msg:', userMsgError);

      // 3. Get AI Response from Flask Backend
      const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const response = await fetch(`${apiBase}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: userText,
          userId: user.id 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const aiMsg = {
          role: 'assistant',
          text: data.answer,
          timestamp: new Date().toISOString()
        };
        // Update local UI
        setMessages((m) => [...m, aiMsg]);

        // 4. Save AI Response to Supabase
        const { error: aiMsgError } = await supabase
          .from('chat_messages')
          .insert([{
            session_id: currentSessionId,
            role: 'assistant',
            content: data.answer,
            user_id: user.id
          }]);

        if (aiMsgError) console.error('Error saving ai msg:', aiMsgError);

      } else {
        const errorMsg = {
          role: 'error',
          text: data.error || 'An error occurred',
          timestamp: new Date().toISOString()
        };
        setMessages((m) => [...m, errorMsg]);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMsg = {
        role: 'error',
        text: 'Failed to connect to server',
        timestamp: new Date().toISOString()
      };
      setMessages((m) => [...m, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset input so the same file can be re-selected if needed
    e.target.value = '';

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUploadStatus('error');
      setMessages((m) => [...m, {
        role: 'error',
        text: 'Only PDF files are supported for upload.',
        timestamp: new Date().toISOString()
      }]);
      setTimeout(() => setUploadStatus(null), 3000);
      return;
    }

    setUploadStatus('uploading');
    setShowWelcome(false);
    setMessages((m) => [...m, {
      role: 'user',
      text: `📎 Uploading file: ${file.name}...`,
      timestamp: new Date().toISOString()
    }]);

    try {
      const formData = new FormData();
      formData.append('files', file);

      const response = await fetch('http://localhost:5000/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setUploadStatus('success');
        setMessages((m) => [...m, {
          role: 'assistant',
          text: `✅ File "${file.name}" uploaded successfully! You can now ask questions about this document.`,
          timestamp: new Date().toISOString()
        }]);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      setUploadStatus('error');
      setMessages((m) => [...m, {
        role: 'error',
        text: `❌ Upload failed: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setTimeout(() => setUploadStatus(null), 3000);
    }
  };

  const grouped = groupChatsByTime(chats);

  return (
    <div className="landing-container">
      {/* Main Chat Area */}
      <div className={`chat-main ${!sidebarVisible ? 'full-width' : ''}`}>
        {/* Sidebar Toggle Button (Moved to Top-Left) */}
        <button 
          className="sidebar-toggle-btn" 
          onClick={() => setSidebarVisible(!sidebarVisible)}
          title={sidebarVisible ? "Hide history" : "Show history"}
        >
          {sidebarVisible ? <Clock size={20} /> : <MessageCircle size={20} />}
        </button>

        {showWelcome && messages.length === 0 ? (
          /* Welcome Screen */
          <div className="welcome-section">
            {/* Animated Glassmorphic Orb */}
            <div className="welcome-orb-wrapper">
              <div className="welcome-orb" />
            </div>

            {/* Greeting */}
            <div className="welcome-header">
              <div className="welcome-greeting">
                {getGreeting()}, {userName}
              </div>
              <div className="welcome-assist">
                How Can I <span className="accent">Assist You</span> Today?
              </div>
            </div>

            {/* Input bar (centered on welcome) */}
            <div className="chat-input-container">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <form className="chat-input-form" onSubmit={handleSubmit}>
                <button
                  type="button"
                  className={`input-icon-btn${uploadStatus === 'uploading' ? ' uploading' : ''}`}
                  title="Attach PDF file"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadStatus === 'uploading'}
                >
                  <Paperclip size={18} />
                </button>
                <input
                  type="text"
                  className="chat-input"
                  data-tour="Chat Input Box"
                  placeholder="Initiate a query or send a command to the AI..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!query.trim() || isLoading}
                >
                  <Send size={16} />
                </button>
              </form>

              {/* Suggestion chips below input */}
              <div className="prompt-suggestions">
                <div className="suggestions-chips">
                  {promptSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="suggestion-chip"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <>
            <div className="chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className={`message-wrapper ${msg.role}`}>
                  <div className="message-avatar">
                    {msg.role === 'user' ? (
                      profilePhoto ? (
                        <div className="avatar-user" style={{ backgroundImage: `url(${profilePhoto})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
                      ) : (
                        <div className="avatar-user">{user?.name?.[0] || 'U'}</div>
                      )
                    ) : msg.role === 'assistant' ? (
                      <div className="avatar-ai"><Sparkles size={16} /></div>
                    ) : (
                      <div className="avatar-error">!</div>
                    )}
                  </div>
                  <div className="message-content">
                    <div className="message-text">
                      {msg.role === 'assistant' ? (
                        <ReactMarkdown
                          components={{
                            // Open links in new tab
                            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      ) : (
                        msg.text
                      )}
                    </div>
                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message-wrapper assistant">
                  <div className="message-avatar">
                    <div className="avatar-ai"><Sparkles size={16} /></div>
                  </div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input bar (bottom-pinned in chat mode) */}
            <div className="chat-input-container">
              <form className="chat-input-form" onSubmit={handleSubmit}>
                <button
                  type="button"
                  className={`input-icon-btn${uploadStatus === 'uploading' ? ' uploading' : ''}`}
                  title="Attach PDF file"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadStatus === 'uploading'}
                >
                  <Paperclip size={18} />
                </button>
                <input
                  type="text"
                  className="chat-input"
                  data-tour="Chat Input Box"
                  placeholder="Ask anything or upload a document..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!query.trim() || isLoading}
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      {/* Right Sidebar — Chat History */}
      <div className={`chat-sidebar ${sidebarVisible ? 'open' : 'collapsed'}`}>
        {/* Mobile Close Overlay */}
        {sidebarVisible && window.innerWidth <= 1024 && (
          <div className="sidebar-overlay" onClick={() => setSidebarVisible(false)} />
        )}
        <div className="sidebar-header">
          <div className="sidebar-title-group">
            <button className="mobile-close-btn" onClick={() => setSidebarVisible(false)}>
              <Plus size={20} style={{ transform: 'rotate(45deg)' }} />
            </button>
            <h3>Recent Chats</h3>
          </div>
          <button className="new-chat-btn" onClick={handleNewChat} title="New chat">
            <Plus size={16} />
          </button>
        </div>
        <div className="chat-history">
          {chats.length === 0 ? (
            <div className="empty-history">
              <MessageCircle size={28} />
              <p>No chats yet</p>
            </div>
          ) : (
            grouped.map(([label, items]) => (
              <div key={label}>
                <div className="history-time-group">{label}</div>
                {items.map((chat) => (
                  <div
                    key={chat.id}
                    className={`history-item ${activeChatId === chat.id ? 'active' : ''}`}
                    onClick={() => handleChatSelect(chat.id)}
                  >
                    <div className="history-icon">
                      <MessageCircle size={16} />
                    </div>
                    <div className="history-content">
                      {renamingChatId === chat.id ? (
                        <form onSubmit={(e) => handleRenameSubmit(chat.id, e)} className="rename-form">
                          <input
                            type="text"
                            className="rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleRenameSubmit(chat.id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        </form>
                      ) : (
                        <>
                          <h4 className="history-title">{chat.title || 'Untitled Chat'}</h4>
                          <p className="history-preview">{chat.last_message || 'No messages'}</p>
                        </>
                      )}
                    </div>
                    <div className="history-actions">
                      <button
                        className="history-menu-btn"
                        onClick={(e) => toggleMenu(chat.id, e)}
                        title="More options"
                      >
                        <MoreVertical size={14} />
                      </button>
                      {openMenuId === chat.id && (
                        <div className="history-dropdown">
                          <button
                            className="dropdown-item"
                            onClick={(e) => handleRenameChat(chat, e)}
                          >
                            <Edit3 size={13} />
                            <span>Rename</span>
                          </button>
                          <button
                            className="dropdown-item delete"
                            onClick={(e) => handleDeleteChat(chat.id, e)}
                          >
                            <Trash2 size={13} />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
